import qs from 'qs';

import { actions, definitions } from '@luxuryescapes/contract-svc-accommodation';

import { json_headers, request } from './common';
import { HttpResponse } from './types';

export type PropertyResponse = definitions['AdminProperty'];
export type Room = definitions['AdminRoomDetail'];
export type PropertyDetailResponse = definitions['AdminPropertyDetail'];
export type PropertyContentResponse = definitions['PropertyContent'];
export type AdminOfferContentResponse = definitions['AdminOfferContent'];
export type AdminPropertyContentResponse = definitions['AdminPropertyContent'];
export type ProgressStatusResponse = definitions['StatusResponse'];
export type ProgressStatusResponseMeta = Extract<definitions['StatusResponse']['meta'], { propertyId?: string }>;
export type MappedInternalProperty = definitions['AdminMappedInternalProperty'];
export type MappedInternalPropertyWithOffers = definitions['AdminMappedInternalPropertyWithOffers'];
export type InternalProperty = definitions['AdminInternalProperty'];
export type InternalService = definitions['InternalService'];
export type RoomResponse = definitions['AdminRoom'];
export type InternalRoom = definitions['AdminInternalRoom'];
export type SupplierRoom = definitions['AdminSupplierRoom'];
export type MappedInternalRoom = definitions['AdminMappedInternalRoom'];
export type RoomMappingResponse = definitions['AdminPropertyRoomMappings'];
export type RoomMapping = definitions['AdminRoomMapping'];
export type RoomAmenitiesMapping = definitions['AdminRoomAmenitiesMappings'];
export type ExtendedMappedInternalRoom = MappedInternalRoom & { roomId: string };
export type RoomMappingPayload = actions['postAdminPropertyRoomMappings']['parameters']['body']['payload'];
export type ValidationErrorCodes = definitions['ValidationErrorCodes'];
export type ValidationErrorDetails = definitions['ValidationErrorDetails'];
export type VerifyMappedRoomPayload = actions['postAdminVerifyRoomMappings']['parameters']['body']['payload'];
export type SourceModel = definitions['SourceModel'];
export type TargetModel = definitions['TargetModel'];
export type MappingLog = definitions['AdminMappingLog'];
export type VerificationStatusEnum = definitions['VerificationStatusEnum'];
export type PotentialMappingPropertyDetail = definitions['AdminPotentialMappingPropertyDetail'];
export type PotentialMappingDetails = definitions['AdminPotentialMappingDetails'];
export type PropertyStateEnum = definitions['PropertyStateEnum'];
export type AvailabilityResponse =
  actions['unifiedAvailability']['responses']['200']['content']['application/json']['result'];
export type AvailabilityDebugResponse =
  actions['adminUnifiedAvailabilityDebug']['responses']['200']['content']['application/json']['result'];
export type DebugInfo = AvailabilityDebugResponse['debug'];
export type RemovedRate = definitions['RemovedRate'];
export type PricingIssue = definitions['PricingIssue'];
export type MergedRate = definitions['MergedRate'];
export type RoomRates = AvailabilityResponse['properties'][string]['rooms'];
export type RoomRate = RoomRates[string];
export type Rate = definitions['UnifiedRate'];
export type AdminOfferPromptResponse = definitions['AiServicePrompt'];
export type AdminPropertyPromptResponse = definitions['AiServicePrompt'];
export type AdminPrompt = definitions['AdminPrompt'];
export type PropertyDebugResponse = definitions['AdminPropertyDebug'];
export type MakeMeAnOfferStepStatus = Extract<ProgressStatusResponse['meta'], { createOffer?: unknown }>['createOffer'];
// Regular flow meta (create new offer)
export type MakeMeAnOfferRegularMeta = Extract<ProgressStatusResponse['meta'], { createOffer?: unknown }>;
// Prod upsert flow meta (import from production)
export type MakeMeAnOfferProdUpsertMeta = Extract<ProgressStatusResponse['meta'], { validateOffer?: unknown }>;
// Error meta
export type MakeMeAnOfferErrorMeta = Extract<ProgressStatusResponse['meta'], { error?: string }>;
export type AdminFacility = definitions['AdminFacility'];
export type AdminCatalogGroup = definitions['AdminCatalogGroup'];
export type AdminPropertyFacility = definitions['AdminPropertyFacility'];
export type PropertyFacilityAddPayload = actions['addAdminPropertyFacility']['parameters']['body']['payload'];
export type PropertyFacilityUpdatePayload = actions['updateAdminPropertyFacility']['parameters']['body']['payload'];
export type ApproveFacilityPayload = actions['approveDraftFacility']['parameters']['body']['payload'];
export type MergeFacilityPayload = actions['mergeFacility']['parameters']['body']['payload'];
export type CatalogGroupIcon = definitions['AdminCatalogGroup']['icon'];
export type Icon = definitions['Icon'];
export type ListIconsResponse = actions['listAdminIcons']['responses']['200']['content']['application/json']['result'];
export type AdminAmenity = definitions['AdminAmenity'];
export type AdminRoomCatalogAmenity = definitions['AdminRoomCatalogAmenity'];
export type RoomAmenityAddPayload = actions['addAdminRoomAmenity']['parameters']['body']['payload'];
export type RoomAmenityUpdatePayload = actions['updateAdminRoomAmenity']['parameters']['body']['payload'];
export type ApproveAmenityPayload = actions['approveDraftAmenity']['parameters']['body']['payload'];
export type MergeAmenityPayload = actions['mergeAmenity']['parameters']['body']['payload'];

function basePath() {
  return window.configs.LUX_API_DOMAIN + '/api/accommodation';
}

function baseAdminPath() {
  return window.configs.LUX_API_DOMAIN + '/api/accommodation/admin';
}

export const listProperties = ({
  name,
  id,
  city,
  country,
  rating,
  limit,
  page,
  propertyState,
}: actions['listAdminProperties']['parameters']['query']): Promise<
  HttpResponse<{ total: number; properties: Array<PropertyResponse> }>
> => {
  const url = `${baseAdminPath()}/properties?${qs.stringify({
    name,
    id,
    city,
    country,
    rating,
    limit,
    page,
    propertyState,
  })}`;
  return request(url, {
    method: `GET`,
  });
};

export const getProperty = ({
  propertyId,
}: actions['getAdminProperty']['parameters']['path']): Promise<HttpResponse<PropertyResponse>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}`;
  return request(url, {
    method: `GET`,
  });
};

export const patchProperty = (
  { propertyId }: actions['patchAdminProperty']['parameters']['path'],
  data: actions['patchAdminProperty']['parameters']['body']['payload'],
): Promise<HttpResponse<PropertyResponse>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}`;
  return request(url, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const createProperty = ({
  internalPropertyId,
}: actions['createAdminProperty']['parameters']['body']['payload']): Promise<HttpResponse<ProgressStatusResponse>> => {
  const url = `${baseAdminPath()}/properties`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({ internalPropertyId }),
  });
};

export const listMappedInternalProperties = ({
  propertyId,
  verificationStatuses,
}: actions['getAdminInternalProperties']['parameters']['path'] & {
  verificationStatuses: Array<VerificationStatusEnum>;
}): Promise<HttpResponse<Array<MappedInternalPropertyWithOffers>>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/internal-properties?${qs.stringify({
    verificationStatuses,
  })}`;
  return request(url, {
    method: 'GET',
  });
};

export const getInternalProperty = ({
  internalService,
  internalServiceId,
}: actions['getAdminInternalProperty']['parameters']['path']): Promise<HttpResponse<InternalProperty>> => {
  const url = `${baseAdminPath()}/internalService/${internalService}/internal-properties/${internalServiceId}`;
  return request(url, {
    method: 'GET',
  });
};

export const getUnmappedSuppliers = ({
  name,
  id,
  internalService,
  limit,
  page,
  verificationStatuses,
}: actions['getAdminUnmappedInternalProperties']['parameters']['query']): Promise<
  HttpResponse<{ total: number; internalProperties: Array<InternalProperty> }>
> => {
  const url = `${baseAdminPath()}/properties/unmapped-internal-properties?${qs.stringify({
    name,
    id,
    internalService,
    limit,
    page,
    verificationStatuses,
  })}`;
  return request(url, {
    method: 'GET',
  });
};

export const patchInternalProperty = (
  { propertyId, internalPropertyId }: actions['patchAdminInternalProperty']['parameters']['path'],
  data: actions['patchAdminInternalProperty']['parameters']['body']['AdminPatchSupplierProperty'],
): Promise<HttpResponse<InternalProperty>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/internal-properties/${internalPropertyId}`;
  return request(url, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const listRooms = ({
  propertyId,
}: actions['getAdminRooms']['parameters']['path']): Promise<HttpResponse<Array<RoomResponse>>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/rooms`;
  return request(url, {
    method: 'GET',
  });
};

export const patchRoom = (
  { propertyId, roomId }: actions['patchAdminRoom']['parameters']['path'],
  data: actions['patchAdminRoom']['parameters']['body']['payload'],
): Promise<HttpResponse<RoomResponse>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/rooms/${roomId}`;
  return request(url, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const getProgressStatuses = (id: string): Promise<HttpResponse<Array<ProgressStatusResponse>>> => {
  const url = `${basePath()}/progress/statuses?${qs.stringify({ id })}`;
  return request(url, {
    method: `GET`,
  });
};

export const getRoomMappings = (propertyId: string): Promise<HttpResponse<RoomMappingResponse>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/rooms/mappings`;
  return request(url, {
    method: `GET`,
  });
};

export const postRoomMappings = (
  propertyId: string,
  mappings: RoomMappingPayload,
): Promise<HttpResponse<RoomMappingResponse>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/rooms/mappings`;
  return request(url, {
    method: `POST`,
    headers: json_headers,
    body: JSON.stringify(mappings),
  });
};

export const deleteRoomMappings = (
  propertyId: string,
  mappings: RoomMappingPayload,
): Promise<HttpResponse<RoomMappingResponse>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/rooms/mappings`;
  return request(url, {
    method: `DELETE`,
    headers: json_headers,
    body: JSON.stringify(mappings),
  });
};

export const createAndMapRoom = (
  propertyId: string,
  internalRoomIds: Array<string>,
): Promise<HttpResponse<RoomMappingResponse>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/rooms/create-and-map`;
  return request(url, {
    method: `POST`,
    headers: json_headers,
    body: JSON.stringify({ internalRoomIds }),
  });
};

export const verifyMappedRooms = (
  propertyId: string,
  payload: VerifyMappedRoomPayload,
): Promise<HttpResponse<RoomMappingResponse>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/rooms/verify-mappings`;
  return request(url, {
    method: `POST`,
    headers: json_headers,
    body: JSON.stringify(payload),
  });
};

export const getMappingLogs = ({
  sourceModel,
  sourceId,
  targetModel,
  targetId,
}: actions['getMappingLogs']['parameters']['query']): Promise<HttpResponse<Array<definitions['AdminMappingLog']>>> => {
  const url = `${baseAdminPath()}/mapping-logs?${qs.stringify({ sourceModel, sourceId, targetModel, targetId })}`;
  return request(url, {
    method: `GET`,
  });
};

export const mapInternalProperty = ({
  propertyId,
  internalPropertyId,
  validationOverrides,
}: actions['mapInternalProperty']['parameters']['path'] &
  actions['mapInternalProperty']['parameters']['body']['payload']): Promise<HttpResponse<object>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/internal-properties/${internalPropertyId}`;
  return request(url, {
    method: `POST`,
    headers: json_headers,
    body: JSON.stringify({ validationOverrides }),
  });
};

export const unmapInternalProperty = ({
  propertyId,
  internalPropertyId,
}: actions['unmapInternalProperty']['parameters']['path']): Promise<HttpResponse<object>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/internal-properties/${internalPropertyId}`;
  return request(url, {
    method: `DELETE`,
  });
};

export const deleteProperty = (propertyId: string): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}`;
  return request(url, {
    method: 'DELETE',
  });
};

export const getPotentialMappings = ({
  name,
  id,
  internalService,
  page,
  limit,
}: actions['getPotentialMappings']['parameters']['query']): Promise<
  HttpResponse<{ total: number; mappedProperties: Array<MappedInternalProperty> }>
> => {
  const url = `${baseAdminPath()}/properties/potential-mappings?${qs.stringify({
    name,
    id,
    internalService,
    page,
    limit,
  })}`;
  return request(url, {
    method: `GET`,
  });
};

export const getPotentialMappingDetails = ({
  propertyId,
  mappedPropertyId,
}: actions['getMappedPropertyDetails']['parameters']['path']): Promise<HttpResponse<PotentialMappingDetails>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/mapped-properties/${mappedPropertyId}/details`;
  return request(url, {
    method: `GET`,
  });
};

export interface UpdateMappingPayload {
  propertyId: string;
  mappedPropertyId: string;
  verificationStatus: VerificationStatusEnum;
  verifiedComments?: string;
}

export const updateMapping = ({
  propertyId,
  mappedPropertyId,
  verificationStatus,
  verifiedComments,
}: UpdateMappingPayload): Promise<HttpResponse<definitions['AdminMappedInternalProperty']>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/mapped-properties/${mappedPropertyId}`;
  return request(url, {
    method: `PATCH`,
    headers: json_headers,
    body: JSON.stringify({ verificationStatus, verifiedComments }),
  });
};

export const reimportSuppliers = (propertyId: string): Promise<HttpResponse<ProgressStatusResponse>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/suppliers/reimport`;
  return request(url, {
    method: `POST`,
  });
};

export async function deleteRoom({
  propertyId,
  roomId,
}: actions['deleteAdminRoom']['parameters']['path']): Promise<HttpResponse<void>> {
  const url = `${baseAdminPath()}/properties/${propertyId}/rooms/${roomId}`;
  return request(url, {
    method: 'DELETE',
  });
}
export async function initiatePropertyContentGeneration({
  propertyId,
  template,
  examples,
  variables,
  creativity,
  useWebSearch,
}: actions['generatePropertyContent']['parameters']['path'] &
  actions['generatePropertyContent']['parameters']['body']['AdminPrompt']): Promise<
  HttpResponse<ProgressStatusResponse>
> {
  const url = `${baseAdminPath()}/content/properties/${propertyId}/generate-content`;
  return request(url, {
    method: `POST`,
    headers: json_headers,
    body: JSON.stringify({ template, examples, variables, creativity, useWebSearch }),
  });
}

export async function initiateOfferContentGeneration({
  offerId,
  template,
  examples,
  variables,
  creativity,
  includeOfferInGeneration,
  useWebSearch,
}: actions['generateOfferContent']['parameters']['path'] &
  actions['generateOfferContent']['parameters']['body']['AdminPrompt']): Promise<HttpResponse<ProgressStatusResponse>> {
  const url = `${baseAdminPath()}/content/offers/${offerId}/generate-content`;
  return request(url, {
    method: `POST`,
    headers: json_headers,
    body: JSON.stringify({ template, examples, variables, creativity, includeOfferInGeneration, useWebSearch }),
  });
}

export const getAvailability = ({
  checkIn,
  checkOut,
  occupancies,
  propertyIds,
  region,
  currency,
  brand,
  allRates,
  dynamicAvailability,
  useV2,
  source,
}: actions['unifiedAvailability']['parameters']['query']): Promise<HttpResponse<AvailabilityResponse>> => {
  const url = `${basePath()}/properties/availability?${qs.stringify({
    checkIn,
    checkOut,
    occupancies,
    propertyIds,
    region,
    currency,
    brand,
    allRates,
    dynamicAvailability,
    useV2,
    source,
  })}`;
  return request(url, {
    method: 'GET',
  });
};

export const getAvailabilityDebug = ({
  propertyIds,
  checkIn,
  checkOut,
  occupancies,
  region,
  currency,
  brand,
  allRates,
  dynamicAvailability,
  useV2,
  source,
}: actions['adminUnifiedAvailabilityDebug']['parameters']['query']): Promise<
  HttpResponse<AvailabilityDebugResponse>
> => {
  const url = `${basePath()}/admin/properties/availability/debug?${qs.stringify({
    propertyIds,
    checkIn,
    checkOut,
    occupancies,
    region,
    currency,
    brand,
    allRates,
    dynamicAvailability,
    useV2,
    source,
  })}`;
  return request(url, {
    method: 'GET',
  });
};

export async function getPropertyContent({
  propertyId,
}: actions['getAdminPropertyContent']['parameters']['path']): Promise<HttpResponse<AdminPropertyContentResponse>> {
  const url = `${baseAdminPath()}/content/properties/${propertyId}`;
  return request(url, {
    method: `GET`,
  });
}

export const getOfferContent = ({
  offerId,
}: actions['getAdminOfferContent']['parameters']['path']): Promise<HttpResponse<AdminOfferContentResponse>> => {
  const url = `${baseAdminPath()}/content/offers/${offerId}`;
  return request(url, {
    method: `GET`,
  });
};

export const getOfferPrompt = (): Promise<HttpResponse<AdminOfferPromptResponse>> => {
  const url = `${baseAdminPath()}/content/offers/get-prompt`;
  return request(url, {
    method: `GET`,
  });
};

export const getPropertyPrompt = (): Promise<HttpResponse<AdminPropertyPromptResponse>> => {
  const url = `${baseAdminPath()}/content/properties/get-prompt`;
  return request(url, {
    method: `GET`,
  });
};

export const getPropertyDebug = ({
  propertyId,
}: actions['getAdminPropertyDebug']['parameters']['path']): Promise<HttpResponse<PropertyDebugResponse>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/debug`;
  return request(url, {
    method: `GET`,
  });
};

export const refreshPropertyDebug = ({
  propertyId,
}: actions['refreshAdminPropertyDebug']['parameters']['path']): Promise<HttpResponse<PropertyDebugResponse>> => {
  const url = `${baseAdminPath()}/properties/${propertyId}/debug/refresh`;
  return request(url, {
    method: `POST`,
  });
};

type MakeMeAnOfferRequest = actions['makeTestOffer']['parameters']['body']['payload'];
type MakeMeAnOfferResponse = actions['makeTestOffer']['responses']['200']['content']['application/json'];

export async function makeMeAnOffer(payload: MakeMeAnOfferRequest): Promise<MakeMeAnOfferResponse> {
  const url = `${baseAdminPath()}/make-me-a-test-offer`;
  return await request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
}

export async function syncRoomAmenities({
  reservationPropertyId,
  reservationRoomId,
}: {
  reservationPropertyId: string;
  reservationRoomId: string;
}): Promise<HttpResponse<RoomAmenitiesMapping>> {
  const url = `${baseAdminPath()}/room-amenities?reservationPropertyId=${reservationPropertyId}&reservationRoomTypeId=${reservationRoomId}&removeChildAmenities=true`;
  return request(url, {
    method: 'GET',
    headers: json_headers,
  });
}

export const listFacilities = ({
  name,
  catalogGroupId,
  status,
  limit,
  page,
}: actions['listAdminFacilities']['parameters']['query']): Promise<
  HttpResponse<{ facilities: Array<AdminFacility>; total: number }>
> => {
  const url = `${baseAdminPath()}/content/facilities?${qs.stringify({
    name,
    catalogGroupId,
    status,
    limit,
    page,
  })}`;
  return request(url, {
    method: 'GET',
  });
};

export type FacilityUpdatePayload = actions['updateAdminFacility']['parameters']['body']['payload'];
export const updateFacility = (
  { code }: actions['updateAdminFacility']['parameters']['path'],
  data: FacilityUpdatePayload,
): Promise<HttpResponse<AdminFacility>> => {
  const url = `${baseAdminPath()}/content/facilities/${code}`;
  return request(url, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const deleteFacility = ({
  code,
}: actions['deleteAdminFacility']['parameters']['path']): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/content/facilities/${code}`;
  return request(url, {
    method: 'DELETE',
  });
};

export type FacilityCreatePayload = actions['createAdminFacility']['parameters']['body']['payload'];
export const createFacility = (data: FacilityCreatePayload): Promise<HttpResponse<AdminFacility>> => {
  const url = `${baseAdminPath()}/content/facilities`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const listCatalogGroups = ({
  name,
  type,
  limit,
  page,
}: actions['listAdminCatalogGroups']['parameters']['query']): Promise<
  HttpResponse<{ groups: Array<AdminCatalogGroup>; total: number }>
> => {
  const url = `${baseAdminPath()}/catalog-groups?${qs.stringify({
    name,
    type,
    limit,
    page,
  })}`;
  return request(url, {
    method: 'GET',
  });
};

export type CatalogGroupCreatePayload = actions['createAdminCatalogGroup']['parameters']['body']['payload'];
export const createCatalogGroup = (data: CatalogGroupCreatePayload): Promise<HttpResponse<AdminCatalogGroup>> => {
  const url = `${baseAdminPath()}/catalog-groups`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export type CatalogGroupUpdatePayload = actions['updateAdminCatalogGroup']['parameters']['body']['payload'];
export const updateCatalogGroup = (
  { id }: actions['updateAdminCatalogGroup']['parameters']['path'],
  data: CatalogGroupUpdatePayload,
): Promise<HttpResponse<AdminCatalogGroup>> => {
  const url = `${baseAdminPath()}/catalog-groups/${id}`;
  return request(url, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const deleteCatalogGroup = ({
  id,
}: actions['deleteAdminCatalogGroup']['parameters']['path']): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/catalog-groups/${id}`;
  return request(url, {
    method: 'DELETE',
  });
};

// Property Facilities
export const listPropertyFacilities = (
  { propertyId }: actions['listAdminPropertyFacilities']['parameters']['path'],
  query: actions['listAdminPropertyFacilities']['parameters']['query'],
): Promise<HttpResponse<{ facilities: Array<AdminPropertyFacility>; total: number }>> => {
  const url = `${baseAdminPath()}/content/facilities/properties/${propertyId}?${qs.stringify(query)}`;
  return request(url, {
    method: 'GET',
  });
};

export const addPropertyFacility = (
  { propertyId }: actions['addAdminPropertyFacility']['parameters']['path'],
  data: PropertyFacilityAddPayload,
): Promise<HttpResponse<AdminPropertyFacility>> => {
  const url = `${baseAdminPath()}/content/facilities/properties/${propertyId}`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const updatePropertyFacility = (
  { propertyId, facilityCode }: actions['updateAdminPropertyFacility']['parameters']['path'],
  data: PropertyFacilityUpdatePayload,
): Promise<HttpResponse<AdminPropertyFacility>> => {
  const url = `${baseAdminPath()}/content/facilities/properties/${propertyId}/${facilityCode}`;
  return request(url, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const removePropertyFacility = ({
  propertyId,
  facilityCode,
}: actions['removeAdminPropertyFacility']['parameters']['path']): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/content/facilities/properties/${propertyId}/${facilityCode}`;
  return request(url, {
    method: 'DELETE',
  });
};

// Draft Facility Operations
export const approveFacility = (
  { code }: actions['approveDraftFacility']['parameters']['path'],
  data: ApproveFacilityPayload,
): Promise<HttpResponse<AdminFacility>> => {
  const url = `${baseAdminPath()}/content/facilities/${code}/approve`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const discardFacility = ({
  code,
}: actions['discardDraftFacility']['parameters']['path']): Promise<HttpResponse<AdminFacility>> => {
  const url = `${baseAdminPath()}/content/facilities/${code}/discard`;
  return request(url, {
    method: 'POST',
  });
};

export const mergeFacility = (data: MergeFacilityPayload): Promise<HttpResponse<AdminFacility>> => {
  const url = `${baseAdminPath()}/content/facilities/merge`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

// Icons
export const listIcons = (): Promise<HttpResponse<ListIconsResponse>> => {
  const url = `${baseAdminPath()}/content/icons`;
  return request(url, {
    method: 'GET',
  });
};

// Amenities
export const listAmenities = ({
  name,
  catalogGroupId,
  status,
  limit,
  page,
}: actions['listAdminAmenities']['parameters']['query']): Promise<
  HttpResponse<{ amenities: Array<AdminAmenity>; total: number }>
> => {
  const url = `${baseAdminPath()}/content/amenities?${qs.stringify({
    name,
    catalogGroupId,
    status,
    limit,
    page,
  })}`;
  return request(url, {
    method: 'GET',
  });
};

export type AmenityUpdatePayload = actions['updateAdminAmenity']['parameters']['body']['payload'];
export const updateAmenity = (
  { code }: actions['updateAdminAmenity']['parameters']['path'],
  data: AmenityUpdatePayload,
): Promise<HttpResponse<AdminAmenity>> => {
  const url = `${baseAdminPath()}/content/amenities/${code}`;
  return request(url, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const deleteAmenity = ({
  code,
}: actions['deleteAdminAmenity']['parameters']['path']): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/content/amenities/${code}`;
  return request(url, {
    method: 'DELETE',
  });
};

export type AmenityCreatePayload = actions['createAdminAmenity']['parameters']['body']['payload'];
export const createAmenity = (data: AmenityCreatePayload): Promise<HttpResponse<AdminAmenity>> => {
  const url = `${baseAdminPath()}/content/amenities`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

// Property Amenities
export const listPropertyAmenities = (
  { roomId }: actions['listAdminRoomAmenities']['parameters']['path'],
  query: actions['listAdminRoomAmenities']['parameters']['query'],
): Promise<HttpResponse<{ amenities: Array<AdminRoomCatalogAmenity>; total: number }>> => {
  const url = `${baseAdminPath()}/content/amenities/rooms/${roomId}?${qs.stringify(query)}`;
  return request(url, {
    method: 'GET',
  });
};

export const addPropertyAmenity = (
  { roomId }: actions['addAdminRoomAmenity']['parameters']['path'],
  data: RoomAmenityAddPayload,
): Promise<HttpResponse<AdminRoomCatalogAmenity>> => {
  const url = `${baseAdminPath()}/content/amenities/rooms/${roomId}`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const updatePropertyAmenity = (
  { roomId, amenityCode }: actions['updateAdminRoomAmenity']['parameters']['path'],
  data: RoomAmenityUpdatePayload,
): Promise<HttpResponse<AdminRoomCatalogAmenity>> => {
  const url = `${baseAdminPath()}/content/amenities/rooms/${roomId}/${amenityCode}`;
  return request(url, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const removePropertyAmenity = ({
  roomId,
  amenityCode,
}: actions['removeAdminRoomAmenity']['parameters']['path']): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/content/amenities/rooms/${roomId}/${amenityCode}`;
  return request(url, {
    method: 'DELETE',
  });
};

// Draft Amenity Operations
export const approveAmenity = (
  { code }: actions['approveDraftAmenity']['parameters']['path'],
  data: ApproveAmenityPayload,
): Promise<HttpResponse<AdminAmenity>> => {
  const url = `${baseAdminPath()}/content/amenities/${code}/approve`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const discardAmenity = ({
  code,
}: actions['discardDraftAmenity']['parameters']['path']): Promise<HttpResponse<AdminAmenity>> => {
  const url = `${baseAdminPath()}/content/amenities/${code}/discard`;
  return request(url, {
    method: 'POST',
  });
};

export const mergeAmenity = (data: MergeAmenityPayload): Promise<HttpResponse<AdminAmenity>> => {
  const url = `${baseAdminPath()}/content/amenities/merge`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

// Room Amenities
export const listRoomAmenities = (
  { roomId }: { roomId: string },
  query: actions['listAdminRoomAmenities']['parameters']['query'],
): Promise<HttpResponse<{ amenities: Array<AdminRoomCatalogAmenity>; total: number }>> => {
  const url = `${baseAdminPath()}/content/amenities/rooms/${roomId}?${qs.stringify(query)}`;
  return request(url, {
    method: 'GET',
  });
};

export const addRoomAmenity = (
  { roomId }: { roomId: string },
  data: RoomAmenityAddPayload,
): Promise<HttpResponse<AdminRoomCatalogAmenity>> => {
  const url = `${baseAdminPath()}/content/amenities/rooms/${roomId}`;
  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const updateRoomAmenity = (
  { roomId, amenityCode }: { roomId: string; amenityCode: string },
  data: RoomAmenityUpdatePayload,
): Promise<HttpResponse<AdminRoomCatalogAmenity>> => {
  const url = `${baseAdminPath()}/content/amenities/rooms/${roomId}/${amenityCode}`;
  return request(url, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export const removeRoomAmenity = ({
  roomId,
  amenityCode,
}: {
  roomId: string;
  amenityCode: string;
}): Promise<HttpResponse<void>> => {
  const url = `${baseAdminPath()}/content/amenities/rooms/${roomId}/${amenityCode}`;
  return request(url, {
    method: 'DELETE',
  });
};
